/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Pagination } from '@mui/material';
import usePageNavigation from '../../hooks/usePageNavigation';

import DatePickerRange from './DatePicker';

import { queryLimit, RECIEPT_TYPES, PAYMENT_TYPES } from '../../constants';

import { getAllMyReceipts } from '../../services/requests/GraphRequests';

export default function CashLogs(props) {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const { pageNumber = '1' } = useParams();
    const [journals, setJournals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPaymentType, setSelectedPaymentType] = useState('');
    const [selectedStatusType, setSelectedStatusType] = useState('');
    const [selectedCashierId, setSelectedCashierId] = useState('');
    const [selectedCashierName, setSelectedCashierName] = useState('');
    const [selectedCashRegister, setSelectedCashRegister] = useState('');
    const [sortingDates, setSortingDates] = useState({
        postedFrom: null,
        postedTo: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(null);
    const [cashStart, setCashStart] = useState(0);
    const redirectLink = 'client/cash-logs';
    const { organization } = props;

    usePageNavigation(
        setCurrentPage,
        setCashStart,
        pageNumber,
        queryLimit,
        pageCount,
        navigate,
        redirectLink,
    );

    const handlePagination = (event, page) => {
        navigate(`/${redirectLink}/${page}`);
    };

    const getReceipts = () => {
        getAllMyReceipts(
            { ...sortingDates },
            queryLimit,
            cashStart,
            selectedCashRegister.id,
            selectedStatusType,
            selectedPaymentType,
            selectedCashierId,
            selectedCashierName,
        ).then(data => {
            const { data: resData, count: resCount } =
                data.data.getFiscalReceipts;
            setJournals(resData);
            setPageCount(Math.ceil(resCount / queryLimit));
            setLoading(false);
        });
    };

    useEffect(() => {
        getReceipts();
    }, [
        JSON.stringify(sortingDates),
        selectedCashRegister,
        selectedPaymentType,
        selectedStatusType,
        selectedCashierId,
        selectedCashierName,
        currentPage,
    ]);

    const handleChangeCash = event => {
        setSelectedCashRegister(event.target.value);
    };

    if (loading) {
        return (
            <div align='center'>
                <CircularProgress />
            </div>
        );
    }

    if (!organization?.cashRegisters?.length) return null;

    return (
        <Box sx={{ flexGrow: 1, marginTop: 3, marginLeft: 10, marginRight: 3 }}>
            <Grid container spacing={2} align='left'>
                <Grid item xs={6}>
                    <TextField
                        id='standard-select-cash'
                        select
                        label={t('cash_machine')}
                        name='cash'
                        value={selectedCashRegister}
                        onChange={event => handleChangeCash(event)}
                        helperText={t('select_cash_register')}
                        fullWidth
                        sx={{ m: 1 }}
                        variant='standard'
                    >
                        {organization.cashRegisters.map(option => (
                            <MenuItem key={`${option.id}`} value={option}>
                                {`${option.model} ${option.number}`}{' '}
                                {option.id !== 2 ? '(fake)' : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={6}></Grid>
            </Grid>
            <Grid container spacing={2} alignItems='center'>
                <Grid item xs={3} align='center'>
                    <DatePickerRange
                        isCashSelected={!!selectedCashRegister}
                        setSortingDates={setSortingDates}
                    />
                </Grid>
                <Grid item xs={2} align='center'>
                    {!!selectedCashRegister && (
                        <TextField
                            id='input-payment-type'
                            select
                            label={t('payment_type')}
                            name='paymentType'
                            value={selectedPaymentType}
                            onChange={event =>
                                setSelectedPaymentType(event.target.value)
                            }
                            helperText={t('payment_type')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                            align='left'
                        >
                            <MenuItem key='all' value='all'>
                                {t('all').toUpperCase()}
                            </MenuItem>
                            {Object.entries(PAYMENT_TYPES).map(
                                ([key, value]) => (
                                    <MenuItem key={`${value}`} value={`${key}`}>
                                        {t(`${key}`)}
                                    </MenuItem>
                                ),
                            )}
                        </TextField>
                    )}
                </Grid>
                <Grid item xs={3} align='center'>
                    {!!selectedCashRegister && (
                        <TextField
                            id='input-status-type'
                            select
                            label={t('status')}
                            name='statusType'
                            value={selectedStatusType}
                            onChange={event =>
                                setSelectedStatusType(event.target.value)
                            }
                            helperText={t('status')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                            align='left'
                        >
                            <MenuItem key='all' value='all'>
                                {t('all')}
                            </MenuItem>
                            {RECIEPT_TYPES.map((type, index) => (
                                <MenuItem
                                    key={`${type + index}`}
                                    value={`${type}`}
                                >
                                    {t(`${type}_status`)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Grid>
                <Grid item xs={2} align='center'>
                    {!!selectedCashRegister && (
                        <TextField
                            label={t('cashier_id')}
                            name='casheirId'
                            value={selectedCashierId}
                            onChange={event => {
                                setSelectedCashierName('');
                                setSelectedCashierId(event.target.value);
                            }}
                            helperText={t('type_cashier_id')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                        />
                    )}
                </Grid>
                <Grid item xs={2} align='center'>
                    {!!selectedCashRegister && (
                        <TextField
                            label={t('cashier_name')}
                            name='cashierName'
                            value={selectedCashierName}
                            onChange={event => {
                                setSelectedCashierId('');
                                setSelectedCashierName(event.target.value);
                            }}
                            helperText={t('type_cashier_name')}
                            fullWidth
                            sx={{ m: 1 }}
                            variant='standard'
                        />
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={2} align='left'>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('id')}</TableCell>
                                    <TableCell align='right'>
                                        {t('receipt')} #
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('payment_type')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('date_time')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('total')}(MDL)
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('status')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('id_cashier')}
                                    </TableCell>
                                    <TableCell align='right'>
                                        {t('cashier_name')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {journals.map(receipt => {
                                    if (receipt.status === 'synced') {
                                        return (
                                            <TableRow
                                                hover
                                                key={receipt.soapData.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th':
                                                        { border: 0 },
                                                }}
                                                onClick={() => {
                                                    navigate(
                                                        `/client/receipt/${receipt.id}`,
                                                    );
                                                }}
                                            >
                                                <TableCell align='left'>
                                                    {receipt.soapData.id}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.soapData.receipt}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        receipt.soapData.data
                                                            .payments.field[0]
                                                            .type
                                                    }
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.soapData.datetime}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        receipt.soapData.data
                                                            .total
                                                    }
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.status}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.cashier &&
                                                    receipt.cashier.id !== null
                                                        ? receipt.cashier.id
                                                        : ' '}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.cashier &&
                                                    receipt.cashier.name
                                                        ? receipt.cashier.name
                                                        : ' '}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    } else {
                                        return (
                                            <TableRow
                                                key={receipt.soapData.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th':
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell align='left'>
                                                    {receipt.soapData.id}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.soapData.receipt}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        receipt.soapData.data
                                                            .payments.field[0]
                                                            .type
                                                    }
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.soapData.datetime}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {
                                                        receipt.soapData.data
                                                            .total
                                                    }
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.status}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.cashier &&
                                                    receipt.cashier.id !== null
                                                        ? receipt.cashier.id
                                                        : ' '}
                                                </TableCell>
                                                <TableCell align='right'>
                                                    {receipt.cashier &&
                                                    receipt.cashier.name
                                                        ? receipt.cashier.name
                                                        : ' '}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                {pageCount > 1 && (
                    <Grid item xs={11} align='center'>
                        <Pagination
                            sx={{
                                my: 5,
                                justifyContent: 'center',
                                display: 'flex',
                            }}
                            count={pageCount}
                            variant='outlined'
                            shape='rounded'
                            page={+currentPage}
                            onChange={handlePagination}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
